import { useState, useEffect } from 'react';
import { getForResponse, submitAnswer } from '../api/surveyApi';

const getYoutubeEmbedUrl = (url) => {
  if (!url) return null;
  const m = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/|youtube\.com\/shorts\/)([a-zA-Z0-9_-]{11})/);
  return m ? `https://www.youtube.com/embed/${m[1]}` : null;
};

export default function SurveyResponsePage({ branchCode, divisionCode, surveyYear, seqNo }) {
  const [data, setData]       = useState(null);
  const [answers, setAnswers] = useState({});  // { item_no: answer_text 또는 ['1','3'] }
  const [error, setError]     = useState('');
  const [loading, setLoading] = useState(false);
  const [done, setDone]       = useState(false);

  useEffect(() => {
    const load = async () => {
      const res = await getForResponse(branchCode, divisionCode, surveyYear, seqNo);
      if (res.success) setData(res.data);
      else setError(res.message);
    };
    load();
  }, [branchCode, divisionCode, surveyYear, seqNo]);

  const setAnswer = (item_no, val) => setAnswers(p => ({ ...p, [item_no]: val }));

  const toggleCheckbox = (item_no, optName) => {
    setAnswers(p => {
      const cur = Array.isArray(p[item_no]) ? p[item_no] : [];
      const next = cur.includes(optName) ? cur.filter(v => v !== optName) : [...cur, optName];
      return { ...p, [item_no]: next };
    });
  };

  const handleSubmit = async () => {
    setError('');
    const questionItems = data.items.filter(it => it.item_type !== 'S' && it.item_type !== 'V');

    // 필수항목 검증
    for (const it of questionItems) {
      if (it.required_yn === 'Y') {
        const v = answers[it.item_no];
        const empty = !v || (Array.isArray(v) && v.length === 0) || (typeof v === 'string' && v.trim() === '');
        if (empty) { setError(`"${it.item_title}" 항목은 필수 응답입니다.`); return; }
      }
    }

    setLoading(true);
    try {
      const payload = questionItems
        .filter(it => answers[it.item_no] !== undefined)
        .map(it => ({
          item_no: it.item_no,
          answer_text: Array.isArray(answers[it.item_no]) ? answers[it.item_no].join(',') : String(answers[it.item_no])
        }));

      const res = await submitAnswer(branchCode, divisionCode, surveyYear, seqNo, payload);
      if (res.success) setDone(true);
      else setError(res.message);
    } finally {
      setLoading(false);
    }
  };

  if (error && !data) return (
    <div style={S.wrap}>
      <div style={S.card}>
        <div style={{ textAlign: 'center', padding: '40px 0', color: '#ff4d4f' }}>⚠️ {error}</div>
      </div>
    </div>
  );

  if (!data) return (
    <div style={S.wrap}>
      <div style={{ textAlign: 'center', padding: 60, color: '#aaa' }}>로딩 중...</div>
    </div>
  );

  if (done) return (
    <div style={S.wrap}>
      <div style={S.card}>
        <div style={{ textAlign: 'center', padding: '40px 0' }}>
          <div style={{ fontSize: 60, marginBottom: 16 }}>✅</div>
          <h2 style={{ fontSize: 20, fontWeight: 800, marginBottom: 8 }}>제출이 완료되었습니다!</h2>
          <p style={{ fontSize: 14, color: '#888' }}>소중한 의견 감사합니다 😊</p>
        </div>
      </div>
    </div>
  );

  return (
    <div style={S.wrap}>
      <div style={S.header}>
        <div style={{ fontSize: 24, marginBottom: 6 }}>📝</div>
        <h1 style={S.title}>{data.title}</h1>
      </div>

      {data.items.map(item => {
        // 중간제목
        if (item.item_type === 'S') {
          return (
            <div key={item.item_no} style={S.sectionTitle}>
              {item.img_data && (
                <img src={item.img_data} alt="섹션이미지"
                  style={{ width: '100%', borderRadius: 8, marginBottom: 12, maxHeight: 240, objectFit: 'contain', background: '#f5f5f5' }} />
              )}
              <div style={{ fontWeight: 800, fontSize: 18, color: '#1a1a2e' }}>{item.item_title}</div>
            </div>
          );
        }

        // 영상
        if (item.item_type === 'V') {
          const embedUrl = getYoutubeEmbedUrl(item.item_title);
          return (
            <div key={item.item_no} style={S.card}>
              {embedUrl ? (
                <div style={{ aspectRatio: '16/9', width: '100%' }}>
                  <iframe
                    src={embedUrl}
                    style={{ width: '100%', height: '100%', border: 'none', borderRadius: 8 }}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    title={`survey-video-${item.item_no}`} />
                </div>
              ) : (
                <div style={{ color: '#aaa', fontSize: 13 }}>⚠️ 영상을 불러올 수 없습니다.</div>
              )}
            </div>
          );
        }

        return (
          <div key={item.item_no} style={S.card}>
            {item.img_data && (
              <img src={item.img_data} alt="질문이미지"
                style={{ width: '100%', borderRadius: 8, marginBottom: 12, maxHeight: 300, objectFit: 'contain', background: '#f5f5f5' }} />
            )}

            <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 16 }}>
              {item.item_title} {item.required_yn === 'Y' && <span style={{ color: '#ff4d4f' }}>*</span>}
            </div>

            {/* 단답형 */}
            {item.item_type === 'T1' && (
              <input style={S.input}
                value={answers[item.item_no] || ''}
                onChange={e => setAnswer(item.item_no, e.target.value)}
                placeholder="답변을 입력해주세요" />
            )}

            {/* 서술형 */}
            {item.item_type === 'T2' && (
              <textarea style={{ ...S.input, minHeight: 100, resize: 'vertical' }}
                value={answers[item.item_no] || ''}
                onChange={e => setAnswer(item.item_no, e.target.value)}
                placeholder="자유롭게 작성해주세요" />
            )}

            {/* 객관식 (단일선택) */}
            {item.item_type === 'M' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {item.options.map(o => (
                  <button key={o.opt_no}
                    style={{ ...S.optBtn, background: answers[item.item_no] === o.opt_name ? '#1677ff' : '#f5f5f5', color: answers[item.item_no] === o.opt_name ? '#fff' : '#333' }}
                    onClick={() => setAnswer(item.item_no, o.opt_name)}>
                    {o.opt_name}
                  </button>
                ))}
              </div>
            )}

            {/* 체크박스 (다중선택) */}
            {item.item_type === 'C' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {item.options.map(o => {
                  const checked = Array.isArray(answers[item.item_no]) && answers[item.item_no].includes(o.opt_name);
                  return (
                    <button key={o.opt_no}
                      style={{ ...S.optBtn, background: checked ? '#52c41a' : '#f5f5f5', color: checked ? '#fff' : '#333' }}
                      onClick={() => toggleCheckbox(item.item_no, o.opt_name)}>
                      {checked ? '☑ ' : '☐ '}{o.opt_name}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}

      {error && <div style={S.error}>⚠️ {error}</div>}

      <button style={{ ...S.submitBtn, opacity: loading ? 0.7 : 1 }} onClick={handleSubmit} disabled={loading}>
        {loading ? '제출 중...' : '제출하기'}
      </button>
    </div>
  );
}

const S = {
  wrap        : { padding: 16, maxWidth: 520, margin: '0 auto', minHeight: '100vh', background: '#f5f5f5', display: 'flex', flexDirection: 'column', gap: 12 },
  header      : { background: 'linear-gradient(135deg, #1677ff, #4096ff)', borderRadius: 16, padding: '24px 20px', textAlign: 'center' },
  title       : { fontSize: 20, fontWeight: 800, color: '#fff', margin: 0 },
  card        : { background: '#fff', borderRadius: 12, padding: 16, boxShadow: '0 2px 8px rgba(0,0,0,0.06)' },
  sectionTitle: { padding: '16px 4px 8px', borderTop: '10px solid #1677ff', marginTop: 8, marginBottom: 4 },
  optBtn      : { border: 'none', borderRadius: 10, padding: '12px 16px', fontSize: 14, fontWeight: 600, cursor: 'pointer', textAlign: 'left', transition: 'all 0.15s' },
  input       : { border: '1.5px solid #e0e0e0', borderRadius: 8, padding: '10px 12px', fontSize: 14, outline: 'none', width: '100%', boxSizing: 'border-box' },
  error       : { background: '#fff5f5', border: '1px solid #ffcccc', borderRadius: 8, padding: '10px 14px', fontSize: 13, color: '#c0392b' },
  submitBtn   : { width: '100%', background: 'linear-gradient(135deg, #1677ff, #4096ff)', color: '#fff', border: 'none', borderRadius: 12, padding: 16, fontSize: 16, fontWeight: 700, cursor: 'pointer' },
};